import React, { useState, useEffect, useRef } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { Component, CreateComponentDTO, ComponentLayoutConfig } from '@fmeastudio/shared'
import { ComponentType, VerificationStatus, SystemClass } from '@fmeastudio/shared'
import { useComponents } from '@/hooks/useComponents'
import { ThumbnailUpload } from './ThumbnailUpload'
import { ComponentLayoutEditor } from './ComponentLayoutEditor'

export interface ComponentFormProps {
  component?: Component
  onSubmit: (data: CreateComponentDTO) => Promise<void>
  onCancel: () => void
  loading?: boolean
  /** Hide action buttons (when rendered in modal with external footer) */
  hideActions?: boolean
  /** sow-375: Callback when form validity changes (for external submit buttons) */
  onValidityChange?: (isValid: boolean) => void
}

export const ComponentForm: React.FC<ComponentFormProps> = ({
  component,
  onSubmit,
  onCancel,
  loading,
  hideActions,
  onValidityChange
}) => {
  const { data: allComponents } = useComponents()
  const [formData, setFormData] = useState<CreateComponentDTO>({
    name: '',
    type: ComponentType.COMPONENT,
    verificationStatus: VerificationStatus.NOT_STARTED,
    parentId: null,
    description: '',
    // Bugfix (persona-audit Loop 2, #3): systemClass defaults SYSTEM at the
    // schema level, so a parentless create with no explicit choice used to
    // silently become SYSTEM and pollute the Functions/FMEA surfaces (which
    // scope to systemClass SYSTEM). Left `undefined` here — a component with
    // no parent is REQUIRED to pick a class explicitly (see `validate()`); a
    // component with a parent instead inherits the parent's class (see the
    // parentId onChange handler below), so most creates never see this field.
    systemClass: undefined
  })
  // SoW-311: Layout configuration state
  const [layoutConfig, setLayoutConfig] = useState<ComponentLayoutConfig | undefined>(
    component?.layoutConfig || undefined
  )
  // Collapsible section state
  const [layoutExpanded, setLayoutExpanded] = useState(false)

  const [errors, setErrors] = useState<Record<string, string>>({})
  // sow-375: Track touched fields for inline validation
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [cancelButtonHover, setCancelButtonHover] = useState(false)
  const [submitButtonHover, setSubmitButtonHover] = useState(false)
  // Synchronous double-submit guard — mirrors Interlock's RiskIntakeModal
  // `submittingRef` (RiskIntakeModal.tsx). The caller's `loading` prop only
  // flips true on the NEXT render once `onSubmit`'s mutation starts, so a
  // fast double-Enter/double-click on the submit button (native form submit
  // or the external `form="component-form"` button in CreateComponentModal)
  // can invoke `handleSubmit` twice before React re-renders with the
  // disabled button, producing duplicate 201s. Set synchronously at the top
  // of `handleSubmit`, so a second call in the same tick is a no-op.
  const submittingRef = useRef(false)

  useEffect(() => {
    if (component) {
      setFormData({
        name: component.name,
        type: component.type,
        verificationStatus: component.verificationStatus,
        parentId: component.parentId,
        description: component.description || '',
        // Existing rows always have a resolved class (schema defaults SYSTEM);
        // fall back defensively for any pre-migration row missing it.
        systemClass: component.systemClass ?? SystemClass.SYSTEM
      })
      // SoW-311: Sync layout config
      setLayoutConfig(component?.layoutConfig || undefined)
    }
    // sow-375: Reset validation state
    setTouched({})
    setErrors({})
  }, [component])

  // Bugfix (persona-audit): a SYSTEM root or top-level SUBSYSTEM legitimately
  // has no parent, but a COMPONENT or PART with parentId=null has nowhere to
  // inherit a diagram position from — it silently becomes a permanently
  // orphaned node that never renders on the System View canvas (even after
  // reload) and is only discoverable via List view. Require a parent for
  // those two types.
  const parentRequiredForType =
    formData.type === ComponentType.COMPONENT || formData.type === ComponentType.PART

  // sow-375: Validate a single field
  const validateField = (field: string, value: string) => {
    if (field === 'name') {
      if (!value.trim()) return 'Component name is required'
      if (value.length > 100) return 'Name must be 100 characters or less'
    }
    if (field === 'parentId') {
      if (parentRequiredForType && !value) {
        return `Select a parent ${formData.type === ComponentType.PART ? 'component' : 'subsystem'} — otherwise this ${formData.type.toLowerCase()} won't appear on the System View canvas`
      }
    }
    if (field === 'systemClass') {
      if (!formData.parentId && !value) return 'Select a system class'
    }
    return ''
  }

  // sow-375: Handle field blur - validate and mark as touched
  const handleFieldBlur = (field: string, value: string) => {
    setTouched(prev => ({ ...prev, [field]: true }))
    const error = validateField(field, value)
    setErrors(prev => error ? { ...prev, [field]: error } : { ...prev, [field]: '' })
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Component name is required'
    }

    if (formData.name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less'
    }

    // Bugfix (persona-audit): see `parentRequiredForType` above — block
    // submit rather than silently creating an orphaned, invisible node.
    if (parentRequiredForType && !formData.parentId) {
      newErrors.parentId = validateField('parentId', formData.parentId || '')
    }

    // Bugfix (persona-audit Loop 2, #3): a component with no parent has
    // nothing to inherit systemClass from, so it must be chosen explicitly —
    // otherwise it silently defaults SYSTEM server-side (see CreateComponentDTO
    // comment) and pollutes the Functions/FMEA surfaces, which scope to
    // systemClass SYSTEM.
    if (!formData.parentId && !formData.systemClass) {
      newErrors.systemClass = 'Select a system class'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // sow-375: Check if form is valid (for external submit button)
  const isFormValid =
    formData.name.trim().length > 0 &&
    formData.name.length <= 100 &&
    (!parentRequiredForType || !!formData.parentId) &&
    (!!formData.parentId || !!formData.systemClass)

  // sow-375: Notify parent of validity changes
  useEffect(() => {
    if (onValidityChange) {
      onValidityChange(isFormValid)
    }
  }, [isFormValid, onValidityChange])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // sow-375: Touch all fields to show errors
    setTouched({ name: true, systemClass: true, parentId: true })
    if (!validate()) return

    if (submittingRef.current) return
    submittingRef.current = true

    // SoW-311: Include layoutConfig in submission
    const submitData = layoutConfig
      ? { ...formData, layoutConfig }
      : formData
    try {
      await onSubmit(submitData)
    } finally {
      submittingRef.current = false
    }
  }

  // Filter parent options based on type hierarchy
  const parentOptions = allComponents?.filter(c => {
    if (component && c.id === component.id) return false // Can't be its own parent

    // Type hierarchy rules
    if (formData.type === 'SYSTEM') return false // Systems have no parent
    if (formData.type === 'SUBSYSTEM') return ['SYSTEM', 'SUBSYSTEM'].includes(c.type)
    if (formData.type === 'COMPONENT') return ['SYSTEM', 'SUBSYSTEM'].includes(c.type)
    if (formData.type === 'PART') return c.type === 'COMPONENT'

    return false
  })

  const inputStyle = (hasError: boolean = false) => ({
    display: 'block',
    width: '100%',
    padding: '10px 12px',
    borderRadius: 6,
    border: `1px solid ${hasError ? '#ef4444' : '#d1d5db'}`,
    backgroundColor: '#ffffff',
    transition: 'all 150ms',
    color: '#111827',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box' as const
  })

  const labelStyle = {
    display: 'block',
    fontSize: 13,
    fontWeight: 500 as const,
    color: '#374151',
    marginBottom: 4
  }

  return (
    <form id="component-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Two-Column Layout: Thumbnail + Core Fields */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>

        {/* Left Column: Thumbnail (only for existing components) */}
        {component?.id && (
          <div style={{ flexShrink: 0 }}>
            <ThumbnailUpload
              componentId={component.id}
              currentThumbnail={component.thumbnail}
            />
          </div>
        )}

        {/* Right Column: Core Fields */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Component Name */}
          <div>
            <label
              htmlFor="component-form-name"
              style={{ ...labelStyle, display: 'flex', justifyContent: 'space-between' }}
            >
              <span>
                Component Name <span style={{ color: '#ef4444' }}>*</span>
              </span>
              {/* Dogfood fix: past 100 chars the Create/Update button used to
                  silently disable with a "Fill in all required fields"
                  tooltip even though Name was clearly filled — an honest
                  live count (+ the maxLength below, which hard-stops typing
                  at the cap) replaces that silent, misleading failure. */}
              <span
                style={{
                  fontWeight: 400,
                  color: formData.name.length >= 90 ? '#dc2626' : '#9ca3af',
                }}
              >
                {formData.name.length}/100
              </span>
            </label>
            <input
              id="component-form-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Hydraulic Pump, Servo Motor"
              maxLength={100}
              style={inputStyle(touched.name && !!errors.name)}
              onFocus={(e) => {
                const hasError = touched.name && !!errors.name
                e.currentTarget.style.borderColor = hasError ? '#ef4444' : '#3b82f6'
                e.currentTarget.style.boxShadow = hasError ? '0 0 0 2px rgba(239, 68, 68, 0.1)' : '0 0 0 2px rgba(59, 130, 246, 0.1)'
              }}
              onBlur={(e) => {
                handleFieldBlur('name', formData.name)
                const error = validateField('name', formData.name)
                e.currentTarget.style.borderColor = error ? '#ef4444' : '#d1d5db'
                e.currentTarget.style.boxShadow = 'none'
              }}
            />
            {touched.name && errors.name && (
              <p style={{ marginTop: 4, fontSize: 12, color: '#dc2626', margin: '4px 0 0 0' }}>{errors.name}</p>
            )}
          </div>

          {/* Two-column row: Type + Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {/* Component Type */}
            <div>
              <label htmlFor="component-form-type" style={labelStyle}>
                Type <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                id="component-form-type"
                value={formData.type}
                onChange={(e) => {
                  setFormData({ ...formData, type: e.target.value as ComponentType, parentId: null })
                  // Bugfix (persona-audit): re-evaluate/clear the parent
                  // error for the new type instead of carrying a stale
                  // message across a type switch.
                  setErrors(prev => ({ ...prev, parentId: '' }))
                  setTouched(prev => ({ ...prev, parentId: false }))
                }}
                style={inputStyle()}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6'
                  e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.1)'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                <option value="SYSTEM">System</option>
                <option value="SUBSYSTEM">Subsystem</option>
                <option value="COMPONENT">Component</option>
                <option value="PART">Part</option>
              </select>
            </div>

            {/* Verification Status */}
            <div>
              <label htmlFor="component-form-status" style={labelStyle}>
                Status <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                id="component-form-status"
                value={formData.verificationStatus}
                onChange={(e) => setFormData({ ...formData, verificationStatus: e.target.value as VerificationStatus })}
                style={inputStyle()}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6'
                  e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.1)'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                <option value="NOT_STARTED">Not Started</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="VERIFIED">Verified</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>
          </div>

          {/* Parent Component (conditional) */}
          {formData.type !== 'SYSTEM' && (
            <div>
              <label htmlFor="component-form-parent" style={labelStyle}>
                Parent {formData.type === 'SUBSYSTEM' ? 'System' : formData.type === 'COMPONENT' ? 'Subsystem' : 'Component'}
                {/* Bugfix (persona-audit): required for COMPONENT/PART — a
                    parentless component silently becomes an orphaned node
                    that never renders on the System View canvas. */}
                {parentRequiredForType && <span style={{ color: '#ef4444' }}> *</span>}
              </label>
              <select
                id="component-form-parent"
                value={formData.parentId || ''}
                onChange={(e) => {
                  const newParentId = e.target.value || null
                  // Bugfix (persona-audit Loop 2, #3): a child inherits its
                  // parent's systemClass — no independent choice to make (and
                  // no chance of a SYSTEM child hanging off a SUPPORT/EXTERNAL
                  // parent). Clearing the parent drops back to "no class yet",
                  // which `validate()` then requires the user to set.
                  const parent = newParentId
                    ? allComponents?.find(c => c.id === newParentId)
                    : undefined
                  setFormData({
                    ...formData,
                    parentId: newParentId,
                    systemClass: newParentId
                      ? parent?.systemClass ?? formData.systemClass
                      : undefined
                  })
                  if (touched.parentId) {
                    const error = validateField('parentId', newParentId || '')
                    setErrors(prev => ({ ...prev, parentId: error }))
                  }
                }}
                style={inputStyle(touched.parentId && !!errors.parentId)}
                onFocus={(e) => {
                  const hasError = touched.parentId && !!errors.parentId
                  e.currentTarget.style.borderColor = hasError ? '#ef4444' : '#3b82f6'
                  e.currentTarget.style.boxShadow = hasError ? '0 0 0 2px rgba(239, 68, 68, 0.1)' : '0 0 0 2px rgba(59, 130, 246, 0.1)'
                }}
                onBlur={(e) => {
                  handleFieldBlur('parentId', formData.parentId || '')
                  const error = validateField('parentId', formData.parentId || '')
                  e.currentTarget.style.borderColor = error ? '#ef4444' : '#d1d5db'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                <option value="">Select parent...</option>
                {parentOptions?.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.type})
                  </option>
                ))}
              </select>
              {touched.parentId && errors.parentId && (
                <p style={{ marginTop: 4, fontSize: 12, color: '#dc2626', margin: '4px 0 0 0' }}>{errors.parentId}</p>
              )}
            </div>
          )}

          {/* System Class (persona-audit Loop 2, #3) — orphan/parentless
              components used to silently default SYSTEM and pollute the
              Functions/FMEA surfaces (which scope to systemClass SYSTEM).
              A parent's class is inherited (this select is disabled and
              shows the inherited value); a parentless component must pick
              one explicitly. */}
          <div>
            <label htmlFor="component-form-system-class" style={labelStyle}>
              System Class{!formData.parentId && <span style={{ color: '#ef4444' }}> *</span>}
            </label>
            <select
              id="component-form-system-class"
              value={formData.systemClass ?? ''}
              disabled={!!formData.parentId}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  systemClass: (e.target.value || undefined) as CreateComponentDTO['systemClass']
                })
              }
              style={{
                ...inputStyle(touched.systemClass && !!errors.systemClass),
                ...(formData.parentId ? { backgroundColor: '#f3f4f6', color: '#6b7280' } : {})
              }}
              onBlur={() => handleFieldBlur('systemClass', formData.systemClass ?? '')}
            >
              {!formData.parentId && <option value="">Select system class...</option>}
              <option value={SystemClass.SYSTEM}>System (delivered robot)</option>
              <option value={SystemClass.SUPPORT}>Support (test/verification tooling)</option>
              <option value={SystemClass.EXTERNAL}>External (client-side counterparty)</option>
            </select>
            {formData.parentId ? (
              <p style={{ marginTop: 4, fontSize: 12, color: '#6b7280', margin: '4px 0 0 0' }}>
                Inherited from parent
              </p>
            ) : (
              touched.systemClass && errors.systemClass && (
                <p style={{ marginTop: 4, fontSize: 12, color: '#dc2626', margin: '4px 0 0 0' }}>
                  {errors.systemClass}
                </p>
              )
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="component-form-description" style={labelStyle}>Description</label>
            <textarea
              id="component-form-description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the component"
              rows={2}
              style={{
                ...inputStyle(),
                resize: 'vertical',
                minHeight: 60
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6'
                e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = 'none'
              }}
            />
          </div>
        </div>
      </div>

      {/* Collapsible Layout Customization Section (only for existing components) */}
      {component?.id && (
        <div style={{
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          overflow: 'hidden'
        }}>
          {/* Collapsible Header */}
          <button
            type="button"
            onClick={() => setLayoutExpanded(!layoutExpanded)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 16px',
              background: '#f9fafb',
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500,
              color: '#374151',
              textAlign: 'left'
            }}
          >
            {layoutExpanded ? (
              <ChevronDown style={{ width: 18, height: 18, color: '#6b7280' }} />
            ) : (
              <ChevronRight style={{ width: 18, height: 18, color: '#6b7280' }} />
            )}
            Layout Customization
            <span style={{
              marginLeft: 'auto',
              fontSize: 12,
              color: '#9ca3af',
              fontWeight: 400
            }}>
              {layoutExpanded ? 'Click to collapse' : 'Click to expand'}
            </span>
          </button>

          {/* Collapsible Content */}
          {layoutExpanded && (
            <div style={{ padding: 16, borderTop: '1px solid #e5e7eb' }}>
              <ComponentLayoutEditor
                layoutConfig={layoutConfig}
                onChange={setLayoutConfig}
                componentName={formData.name || 'Component'}
                thumbnailUrl={component.thumbnail?.url}
                componentType={formData.type as 'SYSTEM' | 'SUBSYSTEM' | 'COMPONENT' | 'PART'}
                diagramWidth={component.diagramWidth}
                diagramHeight={component.diagramHeight}
              />
            </div>
          )}
        </div>
      )}

      {/* Actions - only show if not hidden */}
      {!hideActions && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12,
          paddingTop: 16,
          marginTop: 8,
          borderTop: '1px solid #e5e7eb'
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              fontSize: 14,
              fontWeight: 500,
              color: '#374151',
              backgroundColor: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              cursor: 'pointer',
              transition: 'all 150ms'
            }}
            onMouseEnter={(e) => {
              setCancelButtonHover(true)
              e.currentTarget.style.backgroundColor = '#f9fafb'
              e.currentTarget.style.borderColor = '#9ca3af'
            }}
            onMouseLeave={(e) => {
              setCancelButtonHover(false)
              e.currentTarget.style.backgroundColor = '#ffffff'
              e.currentTarget.style.borderColor = '#d1d5db'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '10px 20px',
              fontSize: 14,
              fontWeight: 500,
              color: '#ffffff',
              backgroundColor: loading ? '#9ca3af' : '#2563eb',
              border: 'none',
              borderRadius: 6,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              transition: 'all 150ms'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                setSubmitButtonHover(true)
                e.currentTarget.style.backgroundColor = '#1d4ed8'
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                setSubmitButtonHover(false)
                e.currentTarget.style.backgroundColor = '#2563eb'
              }
            }}
          >
            {loading ? 'Saving...' : component ? 'Update Component' : 'Create Component'}
          </button>
        </div>
      )}
    </form>
  )
}
